import type { VideosSection } from "@/models/sun-protection-settings";
import {
    buildPlayerSrc,
    parseVideoEmbed,
    type VideoEmbed,
} from "@kozien/shared";
import { cn } from "@/lib/cn";

type Props = {
    section?: VideosSection | null;
};

type PlayableVideo = {
    key: string;
    title: string;
    description: string | null;
    embed: VideoEmbed;
};

function buildPlayableVideos(section: VideosSection): PlayableVideo[] {
    const videos = section.videos ?? [];
    const playable: PlayableVideo[] = [];
    videos.forEach((video, index) => {
        const embed = parseVideoEmbed(video.youtubeUrl);
        if (!embed) return;
        playable.push({
            key: `${embed.videoId}-${index}`,
            title: video.title ?? "",
            description: video.description ?? null,
            embed,
        });
    });
    return playable;
}

export function VideoHelpSection({ section }: Props) {
    if (!section) return null;

    const title = section.title?.trim() || null;
    const description = section.description?.trim() || null;
    const videos = buildPlayableVideos(section);

    if (!title && !description && videos.length === 0) return null;

    return (
        <section
            className="mt-12 max-w-5xl mx-auto w-full"
            data-testid="video-help-section"
        >
            {(title || description) && (
                <div className="text-center mb-8">
                    {title && (
                        <h2 className="text-2xl font-bold text-gray-900">
                            {title}
                        </h2>
                    )}
                    {description && (
                        <p className="mt-2 text-sm text-gray-500 max-w-xl mx-auto whitespace-pre-line">
                            {description}
                        </p>
                    )}
                </div>
            )}

            {videos.length > 0 && (
                <div
                    className={cn(
                        "grid gap-x-8 gap-y-10",
                        videos.length === 1
                            ? "grid-cols-1 max-w-md mx-auto"
                            : "grid-cols-1 sm:grid-cols-2",
                    )}
                >
                    {videos.map((video) => {
                        return (
                            <article
                                key={video.key}
                                className="flex flex-col"
                                data-testid="video-help-item"
                            >
                                <div className="relative aspect-video rounded-2xl overflow-hidden bg-gray-200">
                                    <iframe
                                        src={buildPlayerSrc(video.embed)}
                                        title={video.title}
                                        className="absolute inset-0 w-full h-full"
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                        allowFullScreen
                                    />
                                </div>

                                {video.title && (
                                    <h3 className="mt-4 text-base font-semibold text-gray-900">
                                        {video.title}
                                    </h3>
                                )}
                                {video.description && (
                                    <p className="mt-1 text-sm text-gray-500 whitespace-pre-line">
                                        {video.description}
                                    </p>
                                )}
                            </article>
                        );
                    })}
                </div>
            )}
        </section>
    );
}
